"use client";

import * as React from "react";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

import { cn } from "@/lib/utils";

function ScrollArea({
  className,
  children,
  scrollFade = false,
  scrollbarGutter = false,
  showOnScroll = false,
  type,
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.Root> & {
  scrollFade?: boolean;
  scrollbarGutter?: boolean;
  showOnScroll?: boolean;
}) {
  const viewportRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!scrollFade && !scrollbarGutter) return;
    const viewport = viewportRef.current;
    if (!viewport) return;

    const update = () => {
      const yStart = Math.max(0, viewport.scrollTop);
      const yEnd = Math.max(
        0,
        viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop
      );
      const xStart = Math.max(0, viewport.scrollLeft);
      const xEnd = Math.max(
        0,
        viewport.scrollWidth - viewport.clientWidth - viewport.scrollLeft
      );

      viewport.style.setProperty("--scroll-area-overflow-y-start", `${yStart}px`);
      viewport.style.setProperty("--scroll-area-overflow-y-end", `${yEnd}px`);
      viewport.style.setProperty("--scroll-area-overflow-x-start", `${xStart}px`);
      viewport.style.setProperty("--scroll-area-overflow-x-end", `${xEnd}px`);

      viewport.toggleAttribute(
        "data-has-overflow-y",
        viewport.scrollHeight > viewport.clientHeight
      );
      viewport.toggleAttribute(
        "data-has-overflow-x",
        viewport.scrollWidth > viewport.clientWidth
      );
    };

    update();
    viewport.addEventListener("scroll", update, { passive: true });

    const ro = new ResizeObserver(update);
    ro.observe(viewport);
    const inner = viewport.firstElementChild;
    if (inner) ro.observe(inner);

    return () => {
      viewport.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, [scrollFade, scrollbarGutter]);

  return (
    <ScrollAreaPrimitive.Root
      data-slot="scroll-area"
      type={type ?? (showOnScroll ? "scroll" : undefined)}
      className={cn("relative", className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        data-slot="scroll-area-viewport"
        className={cn(
          "size-full rounded-[inherit]",
          scrollFade &&
            "mask-t-from-[calc(100%-min(var(--fade-size),var(--scroll-area-overflow-y-start,0)))] mask-b-from-[calc(100%-min(var(--fade-size),var(--scroll-area-overflow-y-end,0)))] mask-l-from-[calc(100%-min(var(--fade-size),var(--scroll-area-overflow-x-start,0)))] mask-r-from-[calc(100%-min(var(--fade-size),var(--scroll-area-overflow-x-end,0)))] [--fade-size:2rem]",
          scrollbarGutter &&
            "data-has-overflow-y:pe-2.5 data-has-overflow-x:pb-2.5"
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      <ScrollBar />
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
}

function ScrollBar({
  className,
  orientation = "vertical",
  ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
  return (
    <ScrollAreaPrimitive.ScrollAreaScrollbar
      data-slot="scroll-area-scrollbar"
      orientation={orientation}
      className={cn(
        "flex touch-none select-none transition-[opacity,transform] duration-150 data-[state=hidden]:opacity-0",
        orientation === "vertical" &&
          "h-full w-2.5 border-l border-l-transparent p-px",
        orientation === "horizontal" &&
          "h-2.5 flex-col border-t border-t-transparent p-px",
        className
      )}
      {...props}
    >
      <ScrollAreaPrimitive.ScrollAreaThumb
        data-slot="scroll-area-thumb"
        className="bg-border relative flex-1 rounded-pill"
      />
    </ScrollAreaPrimitive.ScrollAreaScrollbar>
  );
}

export { ScrollArea, ScrollBar };